import { calculateDistance } from "../components/helpers/calculateDistance";
import Axios from "axios";
import { GET_ADDRESS_FROM_COORDINATES } from "../configs";

/**
 * Detect if device is iOS (iPhone/iPad)
 * @returns {boolean} True if iOS device
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export const calculateDistanceBetweenPoints = (lat1, lng1, lat2, lng2) => {
  return calculateDistance(lng1, lat1, lng2, lat2);
};

/**
 * Check if location has changed (with small threshold to avoid GPS noise)
 * @param {Object} savedLocation - Saved location object with lat, lng
 * @param {Object} currentLocation - Current location object with lat, lng
 * @param {number} thresholdMeters - Distance threshold in meters (default: 100)
 * @returns {boolean} True if location changed
 */
export const hasLocationChangedSignificantly = (
  savedLocation,
  currentLocation,
  thresholdMeters = 100
) => {
  if (!savedLocation || !currentLocation) {
    return false;
  }

  if (!savedLocation.lat || !savedLocation.lng || !currentLocation.lat || !currentLocation.lng) {
    return false;
  }

  const distance = calculateDistanceBetweenPoints(
    parseFloat(savedLocation.lat),
    parseFloat(savedLocation.lng),
    parseFloat(currentLocation.lat),
    parseFloat(currentLocation.lng)
  );

  // Convert km to meters and compare
  return (distance * 1000) > thresholdMeters;
};

/**
 * Check geolocation permission state
 * @returns {Promise<string>} Promise resolving to 'granted', 'denied', 'prompt', or 'unsupported'
 */
export const checkGeolocationPermission = async () => {
  if (!navigator || !navigator.geolocation) {
    return 'unsupported';
  }

  // Check if Permissions API is supported (modern browsers)
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch (error) {
      // Permissions API might not be fully supported, fall back to trying geolocation
      console.warn('Permissions API not fully supported, will try geolocation directly');
    }
  }

  // Fallback: return 'prompt' if we can't check (browser will prompt on first use)
  return 'prompt';
};

/**
 * Check if site is served over HTTPS (required for geolocation in Chrome)
 * @returns {boolean} True if HTTPS or localhost
 */
export const isSecureContext = () => {
  return window.isSecureContext || 
         window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

/**
 * Get current GPS location with improved error handling
 * @param {Object} options - Geolocation options
 * @returns {Promise<Object>} Promise resolving to {lat, lng} or null if failed
 */
export const getCurrentGPSLocation = (options = {}) => {
  return new Promise(async (resolve, reject) => {
    if (!navigator || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }

    // Check if HTTPS is being used (required for Chrome mobile)
    if (!isSecureContext()) {
      reject(new Error("HTTPS_REQUIRED: Geolocation requires HTTPS. Please access the site over HTTPS."));
      return;
    }

    // Check permission state
    const permissionState = await checkGeolocationPermission();
    if (permissionState === 'denied') {
      reject(new Error("PERMISSION_DENIED: Location permission was denied. Please enable location access in your browser settings."));
      return;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        // Provide more specific error messages
        let errorMessage = "Failed to get location";
        let errorCode = "UNKNOWN_ERROR";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorCode = "PERMISSION_DENIED";
            errorMessage = "Location permission was denied. Please enable location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorCode = "POSITION_UNAVAILABLE";
            errorMessage = "Location information is unavailable. Please check your GPS settings.";
            break;
          case error.TIMEOUT:
            errorCode = "TIMEOUT";
            errorMessage = "Location request timed out. Please try again.";
            break;
          default:
            errorCode = "UNKNOWN_ERROR";
            errorMessage = error.message || "Failed to get location";
        }

        // Create enhanced error object
        const enhancedError = new Error(errorMessage);
        enhancedError.code = errorCode;
        enhancedError.originalError = error;
        reject(enhancedError);
      },
      { ...defaultOptions, ...options }
    );
  });
};

/**
 * Convert coordinates to address using Google Maps Geocoding API
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} Promise resolving to location object with formatted_address
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const response = await Axios.post(GET_ADDRESS_FROM_COORDINATES, {
      lat: lat,
      lng: lng,
    });

    return {
      formatted_address: response.data,
      geometry: {
        location: {
          lat: lat,
          lng: lng,
        },
      },
    };
  } catch (error) {
    console.warn("Reverse geocoding failed:", (error.response && error.response.data) || error.message);
    throw error;
  }
};

/**
 * Automatically update location using GPS and reverse geocoding
 * @param {Function} onSuccess - Callback when location is successfully updated
 * @param {Function} onError - Callback when location update fails
 * @returns {Promise<void>}
 */
export const autoUpdateLocation = async (onSuccess, onError) => {
  try {
    // Get current GPS location
    const currentLocation = await getCurrentGPSLocation();

    // Convert to address
    const locationData = await reverseGeocode(currentLocation.lat, currentLocation.lng);

    // Format location object
    const myLocation = [
      {
        formatted_address: locationData.formatted_address,
        geometry: {
          location: {
            lat: locationData.geometry.location.lat,
            lng: locationData.geometry.location.lng,
          },
        },
      },
    ];

    // Save to localStorage
    localStorage.setItem("geoLocation", JSON.stringify(myLocation[0]));
    localStorage.setItem("userLat", myLocation[0].geometry.location.lat.toString());
    localStorage.setItem("userLng", myLocation[0].geometry.location.lng.toString());

    // Create userSetAddress
    const userSetAddress = {
      lat: myLocation[0].geometry.location.lat.toString(),
      lng: myLocation[0].geometry.location.lng.toString(),
      address: myLocation[0].formatted_address,
      house: null,
      tag: null,
    };

    localStorage.setItem("userSetAddress", JSON.stringify(userSetAddress));

    if (onSuccess) {
      onSuccess(userSetAddress);
    }
  } catch (error) {
    console.warn("Auto location update failed:", error);
    if (onError) {
      onError(error);
    }
  }
};

/**
 * Check if location check should be performed (debouncing and caching)
 * @returns {boolean} True if location check should be performed
 */
export const shouldCheckLocation = () => {
  const lastCheckKey = "lastLocationCheck";
  const lastCheckTime = localStorage.getItem(lastCheckKey);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  if (!lastCheckTime) {
    localStorage.setItem(lastCheckKey, Date.now().toString());
    return true;
  }

  const timeSinceLastCheck = Date.now() - parseInt(lastCheckTime, 10);
  
  if (timeSinceLastCheck > CACHE_DURATION) {
    localStorage.setItem(lastCheckKey, Date.now().toString());
    return true;
  }

  return false;
};

